/* Jana-owned reward cleanup only. Native Shopify discount remains authoritative. */
(() => {
  'use strict';
  // BEGIN JANA REWARD RECONCILER
  const createJanaRewardReconciler = ({ giftVariantId, qualifyingVariantId, request }) => {
    const marker = 'jana-bundle-gift-card-v1';
    const giftId = String(giftVariantId || '');
    const qualifyingId = String(qualifyingVariantId || '');
    if (!/^[1-9]\d*$/.test(giftId) || !/^[1-9]\d*$/.test(qualifyingId) || giftId === qualifyingId) throw new Error('Invalid bundle reward configuration.');
    const id = line => String(line.variant_id || line.id || '');
    const quantity = line => Math.max(0, Number(line.quantity) || 0);
    const items = cart => {
      if (!cart || !Array.isArray(cart.items)) throw new Error('Unable to verify the cart. Please try again before checking out.');
      return cart.items;
    };
    const owned = line => id(line) === giftId && line.properties?._jana_reward === marker;
    const recurring = line => Boolean(line.selling_plan_allocation);
    const eligible = cart => items(cart).some(line => id(line) === qualifyingId && quantity(line) > 0);
    const saving = line => {
      const unit = Number(line.original_price ?? line.price);
      const final = Number(line.final_line_price ?? line.line_price);
      return Number.isFinite(unit) && unit > 0 && final === 0;
    };
    const hasFreeCard = cart => items(cart).some(line => owned(line) && quantity(line) === 1 && !recurring(line) && saving(line));
    const updatesFor = (cart, keepOne) => {
      let kept = false;
      const updates = {};
      items(cart).filter(owned).forEach(line => {
        if (!line.key) throw new Error('Unable to verify the reward in your cart.');
        const retain = keepOne && !kept && !recurring(line) && quantity(line) > 0;
        if (retain) kept = true;
        const desired = retain ? 1 : 0;
        if (quantity(line) !== desired) updates[line.key] = desired;
      });
      return updates;
    };
    const isSafe = cart => {
      const rewards = items(cart).filter(owned);
      return rewards.length === 0 || (eligible(cart) && rewards.length === 1 && quantity(rewards[0]) === 1 && !recurring(rewards[0]) && hasFreeCard(cart));
    };
    let queue = Promise.resolve();
    const reconcile = () => {
      const run = async () => {
        let cart = await request('read');
        let changed = false;
        // Normalize duplicates and remove orphan/recurring rewards by line key.
        // Never mutate the qualifying product or an unmarked customer's item.
        let updates = updatesFor(cart, eligible(cart));
        if (Object.keys(updates).length) { cart = await request('update', { updates }); changed = true; }
        // Discounts can be removed or cease to apply independently of quantity.
        if (items(cart).some(owned) && !hasFreeCard(cart)) {
          updates = updatesFor(cart, false);
          if (Object.keys(updates).length) { cart = await request('update', { updates }); changed = true; }
        }
        if (!isSafe(cart)) throw new Error('The bundle reward could not be verified. Please refresh your cart before checking out.');
        return { cart, changed, hasReward: items(cart).some(owned) };
      };
      const result = queue.then(run, run);
      // A failed request must not prevent a later successful verification.
      queue = result.catch(() => {});
      return result;
    };
    return { reconcile, isSafe };
  };
  // END JANA REWARD RECONCILER

  // BEGIN JANA REWARD COORDINATOR
  const createJanaRewardCoordinator = ({ reconcile, onResult = () => {} }) => {
    const pendingNativeUpdates = new Set();
    const check = async () => {
      const result = await reconcile();
      if (result.changed) await onResult(result);
      return result;
    };
    const trackCartUpdate = operation => {
      if (typeof operation !== 'function') throw new Error('A native cart operation is required.');
      // Register before starting the native operation so checkout cannot race it.
      const update = Promise.resolve().then(operation).then(async nativeResult => ({ nativeResult, ...await check() }));
      pendingNativeUpdates.add(update);
      update.then(() => pendingNativeUpdates.delete(update), () => pendingNativeUpdates.delete(update));
      return update;
    };
    const beforeCheckout = async () => {
      while (pendingNativeUpdates.size) await Promise.all(Array.from(pendingNativeUpdates));
      // Always read again. A previously safe render is not checkout authorization.
      return check();
    };
    return { reconcile: check, trackCartUpdate, beforeCheckout };
  };
  // END JANA REWARD COORDINATOR

  const configNode = document.getElementById('jana-reward-cart-config');
  if (!configNode || window.JanaRewardCartGuard || window.Shopify?.designMode || document.querySelector('[data-preview="true"]')) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
  if (config.enabled !== true) return;
  const cartUrl = new URL(config.cartUrl || '/cart', window.location.origin);
  if (cartUrl.origin !== window.location.origin) return;
  const cartPath = cartUrl.pathname.replace(/\/$/, '');
  let renderer = null;
  const request = async (action, body) => {
    const url = new URL(action === 'read' ? `${cartPath}.js` : action === 'change' ? `${cartPath}/change.js` : `${cartPath}/update.js`, window.location.origin);
    const options = { method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } };
    if (body) {
      const payload = { ...body };
      const sections = Array.isArray(config.sections) ? [...config.sections] : [];
      if (config.nativeIntegration) {
        document.querySelectorAll('[data-jana-cart-section]').forEach(node => sections.push(node.dataset.janaCartSection));
        // Refresh the native header count with the same cart response. This
        // request still runs only for the scoped marked-reward cart changes.
        if (document.getElementById('cart-icon-bubble')) sections.push('cart-icon-bubble');
      }
      if (sections.length) {
        payload.sections = Array.from(new Set(sections)).filter(Boolean).slice(0, 5);
        payload.sections_url = window.location.pathname;
      }
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(payload);
    }
    const response = await fetch(url.href, options);
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'Your cart could not be verified. Please try again.');
    return result;
  };
  let reconciler;
  try { reconciler = createJanaRewardReconciler({ giftVariantId: config.giftVariantId, qualifyingVariantId: config.qualifyingVariantId, request }); }
  catch (_) { return; }
  let knownMarkedReward = Boolean(config.hasMarkedReward);
  let needsScopedCheck = () => true;
  const coordinator = createJanaRewardCoordinator({
    reconcile: async () => { const result = await reconciler.reconcile(); knownMarkedReward = result.hasReward; return result; },
    onResult: async result => {
      if (renderer) await renderer(result.cart);
      document.dispatchEvent(new CustomEvent('jana:reward-cart:reconciled', { detail: result }));
    }
  });
  window.JanaRewardCartGuard = {
    ...coordinator,
    setRenderer: callback => { renderer = typeof callback === 'function' ? callback : null; }
  };
  const scopeFor = element => element?.closest('[data-jana-reward-cart]');
  const message = (scope, text) => {
    if (!scope) return;
    if (!scope.isConnected) scope = (scope.id && document.getElementById(scope.id)) || document.querySelector('[data-jana-reward-cart]');
    if (!scope) return;
    let status = scope.querySelector('[data-jana-reward-cart-status]');
    if (!status) {
      status = document.createElement('p');
      status.setAttribute('data-jana-reward-cart-status', '');
      status.setAttribute('role', 'alert');
      status.setAttribute('aria-live', 'polite');
      scope.append(status);
    }
    status.textContent = text;
  };
  let checkoutInFlight = false;
  const allowed = new WeakSet();
  const guardedCheckout = async (scope, resume) => {
    if (checkoutInFlight) return;
    checkoutInFlight = true;
    message(scope, 'Checking your bundle reward…');
    try { await coordinator.beforeCheckout(); message(scope, ''); resume(); }
    catch (error) { message(scope, error.message || 'Your cart could not be verified. Please try again before checking out.'); }
    finally { checkoutInFlight = false; }
  };
  // Only explicitly marked storefront cart forms/links are guarded. No global
  // fetch patch, unrelated form interception, or accelerated-checkout disabling.
  document.addEventListener('submit', event => {
    const form = event.target;
    const scope = scopeFor(form);
    if (!scope || allowed.has(form) || !needsScopedCheck()) return;
    const submitter = event.submitter;
    const isCheckout = submitter?.name === 'checkout' || submitter?.hasAttribute('data-jana-reward-checkout') || form.hasAttribute('data-jana-reward-checkout-form');
    if (!isCheckout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    guardedCheckout(scope, () => {
      const currentForm = form.isConnected ? form : form.id ? document.getElementById(form.id) : null;
      if (!currentForm) throw new Error('Your cart was updated. Please review it and select checkout again.');
      const restoredButton = submitter?.id ? document.getElementById(submitter.id) : null;
      const currentSubmitter = submitter?.isConnected && submitter.form === currentForm ? submitter : restoredButton?.form === currentForm ? restoredButton : currentForm.querySelector('[name="checkout"], [data-jana-reward-checkout]');
      allowed.add(currentForm);
      try { if (currentSubmitter) currentSubmitter.click(); else currentForm.requestSubmit(); }
      finally { allowed.delete(currentForm); }
    });
  }, true);
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[data-jana-reward-checkout]');
    const scope = scopeFor(link);
    if (!scope || allowed.has(link) || !needsScopedCheck()) return;
    const targetUrl = new URL(link.href, window.location.origin);
    if (targetUrl.origin !== window.location.origin || !/\/checkout\/?$/.test(targetUrl.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    guardedCheckout(scope, () => {
      const currentLink = link.isConnected ? link : link.id ? document.getElementById(link.id) : null;
      if (!currentLink) throw new Error('Your cart was updated. Please review it and select checkout again.');
      allowed.add(currentLink);
      try { currentLink.click(); }
      finally { allowed.delete(currentLink); }
    });
  }, true);

  if (config.nativeIntegration) {
    let pendingChanges = 0;
    let mutationQueue = Promise.resolve();
    const hasMarkedReward = () => knownMarkedReward || Boolean(document.querySelector('[data-jana-owned-reward]'));
    needsScopedCheck = () => hasMarkedReward() || pendingChanges > 0;
    const syncExpress = () => {
      const restricted = hasMarkedReward() || pendingChanges > 0;
      document.querySelectorAll('[data-jana-cart-express]').forEach(node => { node.hidden = restricted; node.inert = restricted; });
    };
    const renderCart = cart => {
      if (!cart.sections || typeof cart.sections !== 'object') throw new Error('Your cart changed. Please refresh it before checking out.');
      for (const [sectionId, html] of Object.entries(cart.sections)) {
        if (sectionId === 'cart-icon-bubble') {
          const icon = document.getElementById('cart-icon-bubble');
          if (icon && typeof html === 'string') {
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const freshIcon = parsed.getElementById('cart-icon-bubble') || parsed.getElementById('shopify-section-cart-icon-bubble');
            // The native fragment omits the cart graphic. Refresh only its
            // badge, preserving the graphic, button and native drawer listener.
            // Shopify supplies the visible and localized accessible counts.
            if (freshIcon) {
              const previousBadge = icon.querySelector('.cart-count-bubble');
              const freshBadge = freshIcon.querySelector('.cart-count-bubble');
              if (previousBadge && freshBadge) previousBadge.replaceWith(freshBadge);
              else if (previousBadge) previousBadge.remove();
              else if (freshBadge) icon.append(freshBadge);
            }
          }
          continue;
        }
        const current = Array.from(document.querySelectorAll('[data-jana-cart-section]')).find(node => node.dataset.janaCartSection === sectionId);
        if (!current) continue;
        if (typeof html !== 'string') throw new Error('Your cart could not be refreshed. Please reload it before checking out.');
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = Array.from(parsed.querySelectorAll('[data-jana-cart-section]')).find(node => node.dataset.janaCartSection === sectionId);
        if (!fresh) throw new Error('Your cart could not be refreshed. Please reload it before checking out.');
        if (current.tagName === 'CART-DRAWER') {
          // Keep the native modal/overlay instance and open state. Replace only
          // its three observed server-rendered fragments, letting child custom
          // elements initialize naturally with the new form and line controls.
          for (const selector of ['.drawer__header', '.cart-drawer__body', '.drawer__footer']) {
            const before = current.querySelector(selector);
            const after = fresh.querySelector(selector);
            if (before && after) before.replaceWith(after);
          }
          current.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
          const warnings = current.querySelector('.drawer__inner-empty');
          const freshWarnings = fresh.querySelector('.drawer__inner-empty');
          if (warnings) warnings.remove();
          if (freshWarnings) current.querySelector('.drawer__inner').prepend(freshWarnings);
        } else {
          current.replaceWith(fresh);
        }
      }
      knownMarkedReward = cart.items.some(line => String(line.variant_id || line.id) === String(config.giftVariantId) && line.properties?._jana_reward === 'jana-bundle-gift-card-v1');
      syncExpress();
    };
    renderer = renderCart;
    const changeLine = (scope, lineKey, quantity, focusId) => {
      const previous = mutationQueue;
      pendingChanges += 1;
      syncExpress();
      message(scope, 'Updating your cart…');
      const operation = coordinator.trackCartUpdate(async () => {
        await previous.catch(() => {});
        return request('change', { id: lineKey, quantity });
      });
      mutationQueue = operation;
      operation.then(result => {
        // Cleanup already rendered its final response; otherwise render the
        // requested native quantity change, including its Shopify sections.
        if (!result.changed) renderCart(result.nativeResult);
        message(scope, '');
        if (focusId) document.getElementById(focusId)?.focus();
      }).catch(error => message(scope, error.message || 'Your cart could not be updated. Please try again.')).finally(() => {
        pendingChanges -= 1;
        syncExpress();
      });
    };
    document.addEventListener('change', event => {
      const input = event.target.closest?.('[data-jana-cart-quantity]');
      const scope = scopeFor(input);
      if (!scope || !needsScopedCheck()) return;
      event.stopImmediatePropagation();
      const quantity = Number(input.value);
      if (!Number.isInteger(quantity) || quantity < 0 || !input.dataset.janaCartLine) {
        message(scope, 'Please enter a valid quantity.');
        return;
      }
      changeLine(scope, input.dataset.janaCartLine, quantity, input.id);
    }, true);
    document.addEventListener('click', event => {
      const remove = event.target.closest?.('[data-jana-cart-remove]');
      const scope = scopeFor(remove);
      if (!scope || !needsScopedCheck()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (remove.dataset.janaCartLine) changeLine(scope, remove.dataset.janaCartLine, 0);
    }, true);
    syncExpress();
    const checkVisibleCart = () => {
      if (!hasMarkedReward()) return;
      const scope = document.querySelector('[data-jana-reward-cart]');
      if (scope) coordinator.reconcile().catch(error => message(scope, error.message));
    };
    checkVisibleCart();
    window.addEventListener('pageshow', event => { if (event.persisted) checkVisibleCart(); });
    // Native app additions can replace these server sections. Refresh only the
    // scoped accelerated-control visibility; never intercept unrelated requests.
    const observer = new MutationObserver(syncExpress);
    const observationRoots = new Set(Array.from(document.querySelectorAll('[data-jana-cart-section]')).map(node => node.closest('.shopify-section') || node.parentElement).filter(Boolean));
    observationRoots.forEach(node => observer.observe(node, { childList: true, subtree: true }));
  }
})();
